/**
 * OperatorSwitcher — the header "Operator" scope control (TRACK D · F4.5 / D5.4).
 *
 * Replaces the decorative "All Marketplaces" ProfileSwitcher in the top bar with
 * a switcher that scopes the whole app to a *book of products*:
 *
 *   - My products    — the products this employee owns (scope token 'mine'),
 *   - Whole catalog  — every product (scope token 'all', the default == today),
 *   - <a teammate>   — view another employee's book (scope token = their slug).
 *
 * Naming: "Operator", never "Portfolio" — "portfolio" already means an Amazon Ads
 * portfolio (ppc_portfolio_definitions, migration 061). The scope itself is the
 * server-persisted, per-operator selection from usePortfolioScope (F4.4). The
 * people come from GET /api/brief/tabs (PR 8, D2) — the same list as the Daily
 * Brief's person chips: people only (service and test accounts such as
 * "clickpay-mcp-full" are dropped by the server), and "me" is the server's
 * `viewer` slug, so the current employee is labelled "My products" and left out
 * of the teammate list (lib/operatorList.ts).
 *
 * NULL-tolerant: with no operators (empty list / auth off / unidentified caller)
 * the switcher still offers My products + Whole catalog and renders cleanly — it
 * never crashes on partial/empty data, matching the F4.1/F4.4 contract. A stored
 * scope pointing at a since-removed teammate coerces to a labelled fallback.
 *
 * Single-select (unlike ProfileSwitcher's multi-marketplace checkboxes): exactly
 * one scope is active at a time, so the menu uses radio-style rows with a check
 * on the active option. The marketplace narrowing that USED to live in the top
 * bar moves to the per-page Marketplace dropdown (F4.3).
 */
import { useEffect, useState, useRef } from 'react'
import { ChevronDown, Check, User, Users } from 'lucide-react'
import { api, type BriefTabsResponse } from '../lib/api'
import { switcherOptions } from '../lib/operatorList'
import { usePortfolioScope, type PortfolioScope } from '../lib/usePortfolioScope'

/** The two synthetic scope tokens (everything else is a concrete operator slug). */
const SCOPE_MINE: PortfolioScope = 'mine'
const SCOPE_ALL: PortfolioScope = 'all'

export default function OperatorSwitcher() {
  const { scope, setScope, hydrated } = usePortfolioScope()
  const [tabs, setTabs] = useState<BriefTabsResponse | null>(null)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // The people list. NULL-tolerant: a failed fetch / empty table leaves the
  // switcher with just My products + Whole catalog (still fully usable).
  useEffect(() => {
    let alive = true
    api.getBriefTabs()
      .then(r => { if (alive) setTabs(r) })
      .catch(() => { /* unidentified / offline — synthetic scopes still work */ })
    return () => { alive = false }
  }, [])

  // Close on outside click — same idiom as ProfileSwitcher.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  // "me" is the server-resolved viewer slug, so the current employee shows as
  // "My products" rather than in the teammate list. In open mode (auth off)
  // there is no viewer → no self, and every person is a selectable book —
  // harmless, since attribution follows the session.
  const { me, teammates } = switcherOptions(tabs)

  // The active scope's human label for the header chip.
  const activeTeammate = teammates.find(o => o.slug === scope)
  const chipLabel =
    scope === SCOPE_MINE ? 'My products'
      : scope === SCOPE_ALL ? 'Whole catalog'
        : activeTeammate ? activeTeammate.display_name
          // A stored slug that no longer resolves to a teammate (removed/renamed):
          // show it verbatim so the operator can see + correct the stale choice.
          : scope

  function choose(next: PortfolioScope) {
    setScope(next)
    setOpen(false)
  }

  return (
    <div ref={ref} className="relative" data-testid="operator-switcher">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100"
        title="Operator — scope the app to a book of products"
      >
        <span className="w-6 h-6 rounded bg-violet-600 text-white flex items-center justify-center ring-2 ring-white">
          {scope === SCOPE_ALL
            ? <Users size={13} />
            : scope === SCOPE_MINE
              ? <User size={13} />
              : <span className="text-xs font-bold">{chipLabel.charAt(0).toUpperCase()}</span>}
        </span>
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[10px] uppercase tracking-wider text-gray-400">Operator</span>
          <span className="text-sm font-medium text-gray-900 -mt-0.5">{chipLabel}</span>
        </span>
        <ChevronDown size={14} className="text-gray-500" />
      </button>
      {open && (
        // Phone (W0.5): below md, span the screen under the switcher - the
        // wrapped header can leave too little room on its left for 18rem.
        <div className="absolute right-0 top-full mt-1 w-72 max-md:fixed max-md:inset-x-2 max-md:top-auto max-md:w-auto bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="px-3 py-2 text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
            Operator
          </div>
          <ScopeRow
            label="My products"
            hint={me ? me.display_name : 'Products you own'}
            icon={<User size={13} />}
            checked={scope === SCOPE_MINE}
            onClick={() => choose(SCOPE_MINE)}
          />
          <ScopeRow
            label="Whole catalog"
            hint="Every product across the team"
            icon={<Users size={13} />}
            checked={scope === SCOPE_ALL}
            onClick={() => choose(SCOPE_ALL)}
          />
          {teammates.length > 0 && (
            <div className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-gray-400 border-t border-gray-100">
              Teammates
            </div>
          )}
          {teammates.map(o => (
            <ScopeRow
              key={o.slug}
              label={o.display_name}
              hint={`${o.slug}'s book`}
              avatar={o.display_name.charAt(0).toUpperCase()}
              avatarColor={o.color ?? undefined}
              checked={scope === o.slug}
              onClick={() => choose(o.slug)}
            />
          ))}
          {!hydrated && (
            <div className="px-3 py-2 text-[11px] text-gray-400 border-t border-gray-100">
              Syncing your saved view…
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** One selectable scope row — radio-style (single active scope), mirroring the
 *  ProfileSwitcher option row layout (icon/avatar · label · check). */
function ScopeRow({
  label, hint, icon, avatar, avatarColor, checked, onClick,
}: {
  label: string
  hint?: string
  icon?: React.ReactNode
  avatar?: string
  avatarColor?: string
  checked: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
    >
      <span
        className={`w-6 h-6 rounded text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${
          avatarColor ? '' : 'bg-violet-600'
        }`}
        style={avatarColor ? { backgroundColor: avatarColor } : undefined}
      >
        {avatar ?? icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block truncate text-gray-900">{label}</span>
        {hint && <span className="block truncate text-xs text-gray-500">{hint}</span>}
      </span>
      {checked && <Check size={15} className="text-violet-600 flex-shrink-0" />}
    </button>
  )
}
